import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import {
  Add,
  Delete,
  CalendarToday,
  Assessment,
  AccessTime,
  Event,
  DragIndicator,
} from "@mui/icons-material";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { sprintsAPI, dashboardAPI } from "../../services/api";

// Draggable Change Request Component
const DraggableChangeRequest = ({ request }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `change-request-${request.id}`,
      data: { type: "change-request", request },
    });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    opacity: isDragging ? 0.5 : 1,
    cursor: "grab",
  };

  return (
    <Card ref={setNodeRef} style={style} sx={{ mb: 2, position: "relative" }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1}>
          <DragIndicator
            sx={{ color: "text.secondary", cursor: "grab" }}
            {...listeners}
            {...attributes}
          />
          <Box flex={1}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {request.title}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
              {request.estimated_hours && (
                <Chip label={`${request.estimated_hours}h`} size="small" />
              )}
              {request.estimated_days && (
                <Chip label={`${request.estimated_days}d`} size="small" />
              )}
              {request.estimated_story_points && (
                <Chip
                  label={`${request.estimated_story_points} SP`}
                  size="small"
                />
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Droppable Sprint Component
const DroppableSprint = ({ sprint, children, onAllocate }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `sprint-${sprint.id}`,
    data: { type: "sprint", sprint },
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        border: isOver ? "2px dashed" : "none",
        borderColor: isOver ? "primary.main" : "transparent",
        borderRadius: 1,
        p: isOver ? 1 : 0,
        transition: "all 0.2s",
      }}
    >
      {children}
    </Box>
  );
};

const SprintPlanning = ({ activeConfigurationId }) => {
  const [sprints, setSprints] = useState([]);
  const [unallocatedRequests, setUnallocatedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sprintDialogOpen, setSprintDialogOpen] = useState(false);
  const [selectedSprint, setSelectedSprint] = useState(null);
  const [sprintDetails, setSprintDetails] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [iterations, setIterations] = useState([]);
  const [sprintForm, setSprintForm] = useState({
    name: "",
    description: "",
    start_date: "",
    end_date: "",
    status: "Planning",
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor),
  );

  useEffect(() => {
    if (activeConfigurationId) {
      loadData();
      loadIterations();
    } else {
      setUnallocatedRequests([]);
      setLoading(false);
      setIterations([]);
    }
  }, [activeConfigurationId]);

  const loadIterations = async () => {
    if (!activeConfigurationId) {
      setIterations([]);
      return;
    }

    try {
      const response = await dashboardAPI.getWorkItems(activeConfigurationId);
      const workItems = response.data.workItems || [];
      
      // Extract unique iterations from work items
      const uniqueIterations = new Set();
      workItems.forEach((item) => {
        if (item.iteration_path) {
          // Extract iteration name (last part after backslash)
          const iterationName = item.iteration_path.split("\\").pop();
          if (iterationName && iterationName.trim()) {
            uniqueIterations.add(iterationName.trim());
          }
        }
      });
      
      // Convert to sorted array
      const sortedIterations = Array.from(uniqueIterations).sort();
      setIterations(sortedIterations);
    } catch (err) {
      console.error("Failed to load iterations:", err);
      setIterations([]);
    }
  };

  const loadData = async () => {
    if (!activeConfigurationId) {
      setUnallocatedRequests([]);
      return;
    }

    try {
      setLoading(true);
      const [sprintsRes, unallocatedRes] = await Promise.all([
        sprintsAPI.getAll(),
        sprintsAPI.getUnallocated(activeConfigurationId),
      ]);
      setSprints(sprintsRes.data);
      setUnallocatedRequests(unallocatedRes.data);
    } catch (err) {
      setError("Failed to load sprint data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSprint = () => {
    // Set default dates: today for start, 2 weeks from today for end
    const today = new Date();
    const twoWeeksLater = new Date(today);
    twoWeeksLater.setDate(today.getDate() + 14);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    setSprintForm({
      name: "",
      description: "",
      start_date: formatDate(today),
      end_date: formatDate(twoWeeksLater),
      status: "Planning",
    });
    setSelectedSprint(null);
    setSprintDialogOpen(false);
    setTimeout(() => setSprintDialogOpen(true), 0);
  };

  const handleEditSprint = (sprint) => {
    setSprintForm({
      name: sprint.name,
      description: sprint.description || "",
      start_date: sprint.start_date,
      end_date: sprint.end_date,
      status: sprint.status,
    });
    setSelectedSprint(sprint);
    setSprintDialogOpen(true);
  };

  const handleSaveSprint = async () => {
    try {
      setError("");
      setSuccess("");

      // Debug: Log the form state
      console.log("Form state before validation:", {
        name: sprintForm.name,
        start_date: sprintForm.start_date,
        end_date: sprintForm.end_date,
        nameType: typeof sprintForm.name,
        startDateType: typeof sprintForm.start_date,
        endDateType: typeof sprintForm.end_date,
      });

      // Validate form - check for empty strings explicitly
      const name = sprintForm.name?.trim();
      // Don't trim dates - they might have spaces that are valid
      const startDate = sprintForm.start_date;
      const endDate = sprintForm.end_date;

      if (!name || name === "") {
        setError("Sprint name is required");
        return;
      }

      if (
        !startDate ||
        startDate === "" ||
        startDate === null ||
        startDate === undefined
      ) {
        setError("Start date is required");
        return;
      }

      if (
        !endDate ||
        endDate === "" ||
        endDate === null ||
        endDate === undefined
      ) {
        setError("End date is required");
        return;
      }

      // Prepare the data to send
      const sprintData = {
        name: name,
        description: sprintForm.description?.trim() || "",
        start_date: startDate,
        end_date: endDate,
        status: sprintForm.status || "Planning",
      };

      console.log("Submitting sprint data:", sprintData);

      if (selectedSprint) {
        await sprintsAPI.update(selectedSprint.id, sprintData);
        setSuccess("Sprint updated successfully");
        // Close dialog after a short delay to show success message
        setTimeout(() => {
          setSprintDialogOpen(false);
          setSuccess("");
          loadData();
        }, 1000);
      } else {
        await sprintsAPI.create(sprintData);
        setSuccess("Sprint created successfully");
        // Close dialog after a short delay to show success message
        setTimeout(() => {
          setSprintDialogOpen(false);
          setSuccess("");
          loadData();
        }, 1000);
      }
    } catch (err) {
      console.error("Save sprint error:", err);
      setError(
        err.response?.data?.error || err.message || "Failed to save sprint",
      );
    }
  };

  const handleDeleteSprint = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this sprint? All allocations will be removed.",
      )
    ) {
      return;
    }
    try {
      setError("");
      setSuccess("");
      await sprintsAPI.delete(id);
      setSuccess("Sprint deleted successfully");
      setTimeout(() => {
        setSuccess("");
        loadData();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete sprint");
      setTimeout(() => setError(""), 5000);
    }
  };

  const handleAllocate = async (sprintId, changeRequestId) => {
    try {
      setError("");
      setSuccess("");
      await sprintsAPI.allocateChangeRequest(sprintId, changeRequestId);
      setSuccess("Change request allocated successfully");
      setTimeout(() => {
        setSuccess("");
        loadData();
      }, 2000);
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to allocate change request",
      );
      setTimeout(() => setError(""), 5000);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    if (activeData?.type === "change-request" && overData?.type === "sprint") {
      const changeRequestId = activeData.request.id;
      const sprintId = overData.sprint.id;
      await handleAllocate(sprintId, changeRequestId);
    }
  };

  const getActiveRequest = () => {
    if (!activeId) return null;
    const requestId = activeId.replace("change-request-", "");
    return unallocatedRequests.find((r) => r.id.toString() === requestId);
  };

  const handleRemoveAllocation = async (sprintId, changeRequestId) => {
    try {
      setError("");
      setSuccess("");
      await sprintsAPI.removeAllocation(sprintId, changeRequestId);
      setSuccess("Change request removed from sprint");
      setTimeout(() => {
        setSuccess("");
        loadData();
        if (sprintDetails && sprintDetails.id === sprintId) {
          loadSprintDetails(sprintId);
        }
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to remove allocation");
      setTimeout(() => setError(""), 5000);
    }
  };

  const handleViewSprintDetails = async (sprintId) => {
    try {
      await loadSprintDetails(sprintId);
      setDetailsDialogOpen(true);
    } catch (err) {
      setError("Failed to load sprint details");
    }
  };

  const loadSprintDetails = async (sprintId) => {
    const response = await sprintsAPI.getDetails(sprintId);
    setSprintDetails(response.data);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" component="h2">
          Sprint Planning
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateSprint}
        >
          Create Sprint
        </Button>
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Grid container spacing={3}>
          {/* Sprints List */}
          <Grid item xs={12} md={8}>
            <Typography variant="h6" gutterBottom>
              Sprints
            </Typography>
            {sprints.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No sprints created yet. Create your first sprint to start
                  planning.
                </Typography>
              </Paper>
            ) : (
              <Box>
                {sprints.map((sprint) => (
                  <DroppableSprint
                    key={sprint.id}
                    sprint={sprint}
                    onAllocate={handleAllocate}
                  >
                    <Card sx={{ mb: 2 }}>
                      <CardContent>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Box flex={1}>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={1}
                            >
                              <Typography variant="h6">
                                {sprint.name}
                              </Typography>
                              <Chip
                                label={sprint.status}
                                size="small"
                                color="primary"
                              />
                            </Box>
                            {sprint.description && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                {sprint.description}
                              </Typography>
                            )}
                            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                <Event
                                  fontSize="small"
                                  sx={{ verticalAlign: "middle", mr: 0.5 }}
                                />
                                {new Date(
                                  sprint.start_date,
                                ).toLocaleDateString()}{" "}
                                -{" "}
                                {new Date(sprint.end_date).toLocaleDateString()}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {sprint.change_request_count || 0} Change
                                Requests
                              </Typography>
                            </Box>
                            <Box display="flex" gap={2} flexWrap="wrap">
                              {sprint.total_hours &&
                                parseFloat(sprint.total_hours) > 0 && (
                                  <Chip
                                    icon={<AccessTime />}
                                    label={`${parseFloat(sprint.total_hours).toFixed(1)}h`}
                                    size="small"
                                  />
                                )}
                              {sprint.total_days &&
                                parseFloat(sprint.total_days) > 0 && (
                                  <Chip
                                    icon={<CalendarToday />}
                                    label={`${parseFloat(sprint.total_days).toFixed(1)}d`}
                                    size="small"
                                  />
                                )}
                              {sprint.total_story_points &&
                                parseInt(sprint.total_story_points) > 0 && (
                                  <Chip
                                    icon={<Assessment />}
                                    label={`${parseInt(sprint.total_story_points)} SP`}
                                    size="small"
                                  />
                                )}
                            </Box>
                          </Box>
                          <Box display="flex" gap={1}>
                            <Button
                              size="small"
                              onClick={() => handleViewSprintDetails(sprint.id)}
                            >
                              View Details
                            </Button>
                            <Button
                              size="small"
                              onClick={() => handleEditSprint(sprint)}
                            >
                              Edit
                            </Button>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteSprint(sprint.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </DroppableSprint>
                ))}
              </Box>
            )}
          </Grid>

          {/* Unallocated Change Requests */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              Unallocated Change Requests
            </Typography>
            {unallocatedRequests.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: "center" }}>
                <Typography color="text.secondary">
                  All change requests are allocated or no change requests with
                  estimates available.
                </Typography>
              </Paper>
            ) : (
              <Box>
                {unallocatedRequests.length > 0 && sprints.length > 0 && (
                  <Box mb={2} p={1.5} bgcolor="info.light" borderRadius={1}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      textAlign="center"
                    >
                      💡 Drag change requests to sprints or use the dropdown
                      below
                    </Typography>
                  </Box>
                )}
                {unallocatedRequests.map((request) => (
                  <Box key={request.id}>
                    <DraggableChangeRequest request={request} />
                    {sprints.length > 0 && (
                      <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                        <InputLabel>Or allocate to sprint...</InputLabel>
                        <Select
                          value=""
                          onChange={(e) =>
                            handleAllocate(e.target.value, request.id)
                          }
                          label="Or allocate to sprint..."
                        >
                          {sprints.map((sprint) => (
                            <MenuItem key={sprint.id} value={sprint.id}>
                              {sprint.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Grid>
        </Grid>
        <DragOverlay>
          {activeId ? (
            <Card sx={{ opacity: 0.8, maxWidth: 300 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold">
                  {getActiveRequest()?.title}
                </Typography>
              </CardContent>
            </Card>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Sprint Dialog */}
      <Dialog
        open={sprintDialogOpen}
        onClose={() => setSprintDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedSprint ? "Edit Sprint" : "Create Sprint"}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setSuccess("")}
            >
              {success}
            </Alert>
          )}
          <Autocomplete
            freeSolo
            options={iterations}
            value={sprintForm.name}
            onInputChange={(event, newValue) => {
              setSprintForm({ ...sprintForm, name: newValue || "" });
            }}
            onChange={(event, newValue) => {
              setSprintForm({ ...sprintForm, name: newValue || "" });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Sprint Name"
                margin="normal"
                required
                helperText={
                  iterations.length > 0
                    ? "Select an iteration or type a new sprint name"
                    : "Type a sprint name"
                }
              />
            )}
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={3}
            value={sprintForm.description}
            onChange={(e) =>
              setSprintForm({ ...sprintForm, description: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Start Date"
            type="date"
            margin="normal"
            value={sprintForm.start_date || ""}
            onChange={(e) => {
              const newStartDate = e.target.value;
              setSprintForm({ ...sprintForm, start_date: newStartDate });

              // Auto-update end date to 2 weeks from new start date if end date is empty or before new start date
              if (newStartDate) {
                const start = new Date(newStartDate);
                const newEndDate = new Date(start);
                newEndDate.setDate(start.getDate() + 14);

                const formatDate = (date) => {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, "0");
                  const day = String(date.getDate()).padStart(2, "0");
                  return `${year}-${month}-${day}`;
                };

                if (
                  !sprintForm.end_date ||
                  new Date(sprintForm.end_date) < start
                ) {
                  setSprintForm((prev) => ({
                    ...prev,
                    start_date: newStartDate,
                    end_date: formatDate(newEndDate),
                  }));
                }
              }
            }}
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            fullWidth
            label="End Date"
            type="date"
            margin="normal"
            value={sprintForm.end_date || ""}
            onChange={(e) => {
              setSprintForm({ ...sprintForm, end_date: e.target.value });
            }}
            InputLabelProps={{ shrink: true }}
            required
            inputProps={{
              min: sprintForm.start_date || undefined,
            }}
            helperText={
              sprintForm.start_date &&
              sprintForm.end_date &&
              new Date(sprintForm.end_date) < new Date(sprintForm.start_date)
                ? "End date must be after start date"
                : ""
            }
            error={
              sprintForm.start_date &&
              sprintForm.end_date &&
              new Date(sprintForm.end_date) < new Date(sprintForm.start_date)
            }
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={sprintForm.status}
              onChange={(e) =>
                setSprintForm({ ...sprintForm, status: e.target.value })
              }
              label="Status"
            >
              <MenuItem value="Planning">Planning</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSprintDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSprint} variant="contained">
            {selectedSprint ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sprint Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{sprintDetails?.name} - Details</DialogTitle>
        <DialogContent>
          {sprintDetails && (
            <Box>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {sprintDetails.description}
                </Typography>
                <Box display="flex" gap={2} mt={1}>
                  <Typography variant="body2">
                    <Event
                      fontSize="small"
                      sx={{ verticalAlign: "middle", mr: 0.5 }}
                    />
                    {new Date(sprintDetails.start_date).toLocaleDateString()} -{" "}
                    {new Date(sprintDetails.end_date).toLocaleDateString()}
                  </Typography>
                  <Chip
                    label={sprintDetails.status}
                    size="small"
                    color="primary"
                  />
                </Box>
              </Box>

              <Box mb={3} p={2} bgcolor="grey.50" borderRadius={1}>
                <Typography variant="h6" gutterBottom>
                  Total Effort
                </Typography>
                <Box display="flex" gap={3}>
                  {sprintDetails.totals.total_hours &&
                    parseFloat(sprintDetails.totals.total_hours) > 0 && (
                      <Box>
                        <Typography variant="h4" color="primary">
                          {parseFloat(sprintDetails.totals.total_hours).toFixed(
                            1,
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Hours
                        </Typography>
                      </Box>
                    )}
                  {sprintDetails.totals.total_days &&
                    parseFloat(sprintDetails.totals.total_days) > 0 && (
                      <Box>
                        <Typography variant="h4" color="primary">
                          {parseFloat(sprintDetails.totals.total_days).toFixed(
                            1,
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Days
                        </Typography>
                      </Box>
                    )}
                  {sprintDetails.totals.total_story_points &&
                    parseInt(sprintDetails.totals.total_story_points) > 0 && (
                      <Box>
                        <Typography variant="h4" color="primary">
                          {parseInt(sprintDetails.totals.total_story_points)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Story Points
                        </Typography>
                      </Box>
                    )}
                </Box>
              </Box>

              <Typography variant="h6" gutterBottom>
                Allocated Change Requests (
                {sprintDetails.change_requests?.length || 0})
              </Typography>
              {sprintDetails.change_requests &&
              sprintDetails.change_requests.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell align="right">Hours</TableCell>
                        <TableCell align="right">Days</TableCell>
                        <TableCell align="right">Story Points</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sprintDetails.change_requests.map((cr) => (
                        <TableRow key={cr.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {cr.title}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {cr.estimated_hours || "-"}
                          </TableCell>
                          <TableCell align="right">
                            {cr.estimated_days || "-"}
                          </TableCell>
                          <TableCell align="right">
                            {cr.estimated_story_points || "-"}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleRemoveAllocation(sprintDetails.id, cr.id)
                              }
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary" sx={{ p: 2 }}>
                  No change requests allocated to this sprint yet.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SprintPlanning;
